/**
 * AP-ACCEPT-01B: Supplier Deliveries List
 * Route: /portal/deliveries (protected)
 *
 * Shows all delivery evidence submissions by this supplier.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Package, Plus, RefreshCw, ChevronRight, FileText, AlertCircle, CheckCircle2, Clock, XCircle, RotateCcw } from "lucide-react";
import { usePortalUser } from "@/components/PortalProtectedRoute";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  PENDING_REVIEW:       { label: "Pending Review", bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  UNDER_REVIEW:         { label: "Under Review",   bg: "#EFF6FF", text: "#1E40AF", border: "#BFDBFE" },
  ACCEPTED:             { label: "Accepted",        bg: "#F0FDF4", text: "#166534", border: "#BBF7D0" },
  PARTIALLY_ACCEPTED:   { label: "Partially Accepted", bg: "#F0FDF4", text: "#166534", border: "#BBF7D0" },
  REJECTED:             { label: "Rejected",        bg: "#FEF2F2", text: "#991B1B", border: "#FECACA" },
  CORRECTION_REQUESTED: { label: "Correction Requested", bg: "#FFF7ED", text: "#9A3412", border: "#FED7AA" },
  RESUBMITTED:          { label: "Resubmitted",    bg: "#F5F3FF", text: "#5B21B6", border: "#DDD6FE" },
  WITHDRAWN:            { label: "Withdrawn",       bg: "#F5F5F5", text: "#616161", border: "#E0E0E0" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: 600,
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`, letterSpacing: "0.3px",
    }}>{cfg.label}</span>
  );
}

export default function SupplierDeliveries() {
  const { portalUser } = usePortalUser();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 30 };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/api/portal/deliveries`, { params, withCredentials: true });
      setSubmissions(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load deliveries");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="supplier-deliveries-page" style={{ minHeight: "100vh", background: "#FBF7F2", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Nav */}
      <nav style={{ background: "#1A1A1A", borderBottom: "1px solid rgba(201,161,95,0.2)", padding: "0 24px", height: "60px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px", fontWeight: 600, color: "#C9A15F" }}>Prashasta</span>
          <span style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)" }} />
          <span style={{ fontSize: "12px", color: "#8A7A6A", letterSpacing: "1px", textTransform: "uppercase" }}>Partner Portal</span>
        </div>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <Link to="/portal/dashboard" style={{ fontSize: "12px", color: "#8A7A6A", textDecoration: "none" }}>Purchase Orders</Link>
          <Link to="/portal/deliveries" style={{ fontSize: "12px", color: "#C9A15F", textDecoration: "none", fontWeight: 600 }}>My Deliveries</Link>
          <span style={{ fontSize: "11px", color: "#4A3A2A" }}>{portalUser?.email}</span>
        </div>
      </nav>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "clamp(22px, 4vw, 30px)", fontWeight: 600, color: "#1A1A1A", margin: "0 0 4px" }}>My Deliveries</h2>
            <p style={{ fontSize: "13px", color: "#6B5B4A", margin: 0 }}>Track your service delivery submissions and acceptance status</p>
          </div>
          <button
            data-testid="submit-delivery-btn"
            onClick={() => navigate("/portal/deliveries/submit")}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "#C9A15F", color: "#111", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
          >
            <Plus size={15} /> Submit Delivery Evidence
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
          {["", "PENDING_REVIEW", "UNDER_REVIEW", "ACCEPTED", "REJECTED", "CORRECTION_REQUESTED"].map((s) => (
            <button
              key={s || "all"}
              data-testid={`filter-${s || "all"}`}
              onClick={() => setStatusFilter(s)}
              style={{ padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 500, cursor: "pointer", border: "1px solid", transition: "all 0.15s", background: statusFilter === s ? "#1A1A1A" : "transparent", color: statusFilter === s ? "#F5EFE6" : "#6B5B4A", borderColor: statusFilter === s ? "#1A1A1A" : "#D4C5B0" }}
            >
              {s ? (STATUS_CONFIG[s]?.label || s) : "All"}
            </button>
          ))}
          <button onClick={load} style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", background: "transparent", border: "1px solid #D4C5B0", borderRadius: "8px", fontSize: "12px", color: "#6B5B4A", cursor: "pointer" }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {/* List */}
        <div style={{ background: "#FFFFFF", border: "1px solid #EDE5D8", borderRadius: "12px", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: "48px", textAlign: "center" }}>
              <div style={{ width: "28px", height: "28px", border: "2px solid #EDE5D8", borderTopColor: "#C9A15F", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
              <p style={{ color: "#9A8A78", fontSize: "13px" }}>Loading submissions…</p>
            </div>
          ) : submissions.length === 0 ? (
            <div data-testid="no-deliveries-state" style={{ padding: "56px 24px", textAlign: "center" }}>
              <FileText size={36} style={{ color: "#D4C5B0", margin: "0 auto 12px" }} />
              <p style={{ color: "#6B5B4A", fontSize: "14px", fontWeight: 500 }}>No delivery submissions yet</p>
              <p style={{ color: "#9A8A78", fontSize: "13px", marginBottom: "16px" }}>Submit evidence of completed services against your issued Purchase Orders</p>
              <button
                onClick={() => navigate("/portal/deliveries/submit")}
                style={{ padding: "10px 20px", background: "#1A1A1A", color: "#F5EFE6", border: "none", borderRadius: "8px", fontSize: "13px", cursor: "pointer" }}
              >
                Submit your first delivery
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 120px 32px", padding: "10px 20px", background: "#FAF6F0", borderBottom: "1px solid #EDE5D8", fontSize: "10px", fontWeight: 600, color: "#9A8A78", letterSpacing: "0.5px", textTransform: "uppercase" }}
                className="deliveries-header-row"
              >
                <span>PO Reference</span><span>Lines</span><span>Submitted</span><span>Status</span><span />
              </div>
              {submissions.map((s) => (
                <div
                  key={s.id}
                  data-testid={`delivery-row-${s.id}`}
                  onClick={() => navigate(`/portal/deliveries/${s.id}`)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/portal/deliveries/${s.id}`)}
                  className="deliveries-data-row"
                  style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 120px 32px", padding: "14px 20px", borderBottom: "1px solid #F5F0E8", cursor: "pointer", alignItems: "center", transition: "background 0.12s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#FAF6F0")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div>
                    <p style={{ fontSize: "13px", fontWeight: 600, color: "#1A1A1A", margin: 0 }}>{s.po_number || s.po_id?.slice(0, 8)}</p>
                    <p style={{ fontSize: "11px", color: "#9A8A78", margin: "2px 0 0" }}>{s.event_name || "—"}</p>
                    {s.status === "CORRECTION_REQUESTED" && (
                      <span style={{ fontSize: "10px", color: "#9A3412", fontWeight: 600 }}>Action required</span>
                    )}
                  </div>
                  <span className="hide-mobile" style={{ fontSize: "12px", color: "#6B5B4A" }}>{s.line_submissions?.length || 0} line{(s.line_submissions?.length || 0) !== 1 ? "s" : ""}</span>
                  <span className="hide-mobile" style={{ fontSize: "12px", color: "#9A8A78" }}>{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
                  <StatusBadge status={s.status} />
                  <ChevronRight size={14} style={{ color: "#C9A15F", justifySelf: "end" }} />
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 480px) {
  .deliveries-header-row { grid-template-columns: 1fr 90px 32px !important; }
  .deliveries-header-row span:nth-child(2),
  .deliveries-header-row span:nth-child(3) { display: none; }
  .deliveries-data-row { grid-template-columns: 1fr 90px 32px !important; }
  .hide-mobile { display: none !important; }
}
`}</style>
    </div>
  );
}
